import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import * as core from '@fortune-sheet/core';
import { getSheetIndex } from '@fortune-sheet/core';
import isEmpty from 'lodash/isEmpty';
import { splitSparseTail } from './sparseTail';
import { toCelldataSheet } from './converters';
import { findUncachedFormulas, holdDirtySuppression, recalculateUntilStable, UncachedFormula } from './recalcFormulas';
import type { FortuneCellData, FortuneSheet } from './types';
import { getInternals, produceQuietly } from './excel/workbookInternals';

/**
 * Above this many total cells, non-active sheets mount as stubs and hydrate
 * right after first paint. Fortune-Sheet eagerly expands EVERY sheet's celldata
 * into a dense row×column matrix (plus a formula-map pass) at mount, so a large
 * multi-tab workbook pays the full cost before anything renders.
 */
export const LAZY_HYDRATION_CELL_THRESHOLD = 50_000;

// Stub grid dimensions — small enough that the eager dense-matrix expansion of
// a stub costs nothing. updateSheet expands the cells to the payload's size but
// keeps the stub's row/column, so the hydration recipe restores them.
const STUB_ROWS = 50;
const STUB_COLS = 26;

// Hydration cadence: wait a beat after mount so the first paint lands, then
// fill one sheet per tick so no single task blocks input for long.
const FIRST_HYDRATE_DELAY_MS = 120;
const HYDRATE_TICK_MS = 30;

/** A trimmed sheet's calcChain must not point past its shrunken grid: Fortune's
 *  mount-time formula pass throws on calcChain cells outside the data matrix.
 *  Tail formulas keep their `f` and cached value in the parked cells and are not
 *  recalculated while the sheet is open; a kept formula that references a parked
 *  cell reads it as empty. */
const clipCalcChainToGrid = (sheet: FortuneSheet): FortuneSheet => {
    if (!sheet.calcChain?.length || sheet.row == null) return sheet;
    const rowCount = sheet.row;
    return { ...sheet, calcChain: sheet.calcChain.filter((entry) => entry.r < rowCount) };
};

const totalCells = (sheets: FortuneSheet[]): number => sheets.reduce((n, s) => n + (s.celldata?.length ?? 0), 0);

/** Exported for tests. A sheet saved after an older lazy hydration can carry the
 *  50×26 stub size with cells beyond it; Ctrl+Arrow, column auto-fit and the tail
 *  trim read row/column as the grid's edge. Same reference when already covering. */
export const withCoveringDims = (sheet: FortuneSheet): FortuneSheet => {
    if (sheet.row == null || sheet.column == null) return sheet;
    let rows = sheet.data?.length ?? 0;
    let cols = sheet.data?.[0]?.length ?? 0;
    (sheet.celldata ?? []).forEach((cell) => {
        if (cell.r >= rows) rows = cell.r + 1;
        if (cell.c >= cols) cols = cell.c + 1;
    });
    if (rows <= sheet.row && cols <= sheet.column) return sheet;
    return { ...sheet, row: Math.max(sheet.row, rows), column: Math.max(sheet.column, cols) };
};

/** Exported for tests. Keeps id/name/order/config (styling metadata is cheap and
 *  survives hydration — updateSheet's celldata path only swaps cell content in),
 *  drops the cell payload and the calcChain (its cells lie outside the stub
 *  grid, and Fortune's mount-time formula pass would throw on them), and
 *  shrinks the grid to stub dimensions. */
export const makeStubSheet = (sheet: FortuneSheet): FortuneSheet => ({
    ...sheet,
    calcChain: undefined,
    celldata: [],
    row: STUB_ROWS,
    column: STUB_COLS,
});

export interface UseLazySheetsArgs {
    /** Fully-resolved sheets (celldata form). */
    sheets: FortuneSheet[] | null | undefined;
    workbookRef: RefObject<WorkbookInstance>;
    /** Test seam; defaults to LAZY_HYDRATION_CELL_THRESHOLD. */
    cellThreshold?: number;
    /**
     * Set to `true` for the duration of each hydration `updateSheet` call, then
     * back to `false` — `updateSheet` goes through the same `onOp`/emitOp path a
     * real edit does, and a stub-fill's ops are path-identical to a real edit
     * (both write into `data`/`celldata`), so the caller's dirty-tracking (e.g.
     * markWorkbookDirty) reads this ref to tell the two apart. Owned by the
     * caller; a hook-local ref is used when none is passed (read-only viewers
     * with no dirty-tracking, existing tests).
     */
    suppressDirtyRef?: MutableRefObject<boolean>;
}

/**
 * Deferred per-sheet hydration for large workbooks. Below the threshold this is
 * a pass-through. Above it, only the active sheet mounts with content; the
 * other tabs mount as empty stubs, and `hydratePending` (called by the host
 * component right after the Workbook mounts) fills them in one per tick via
 * workbookRef.updateSheet(). Deliberately NOT hooked to tab activation — the
 * first shipped version hydrated on Fortune-Sheet's afterActivateSheet hook,
 * which left every stub blank whenever activation semantics didn't line up
 * (e.g. no sheet carried status === 1, so OUR stubbing and the library's
 * ensureSheetIndex disagreed about which sheet was active).
 *
 * Callers that read sheet content back out (save/download/add-to-reports) MUST
 * still route getAllSheets() through `getFullSheets` — in the brief window
 * before hydration completes, a stub persisted raw would wipe that sheet.
 */
export const useLazySheets = ({
    sheets,
    workbookRef,
    cellThreshold = LAZY_HYDRATION_CELL_THRESHOLD,
    suppressDirtyRef: suppressDirtyRefProp,
}: UseLazySheetsArgs) => {
    // Sheets awaiting hydration for the last COMMITTED render, keyed by sheet
    // id. Written only from the effect below — never from useMemo — so a
    // render React replays or discards can never mutate the queue a committed
    // workbook already has a drain scheduled against (see pendingMap).
    const pendingRef = useRef<Map<string, FortuneSheet>>(new Map());
    const tailRef = useRef<Map<string, FortuneCellData[]>>(new Map());
    const uncachedRef = useRef<UncachedFormula[]>([]);
    const ownSuppressDirtyRef = useRef(false);
    const suppressDirtyRef = suppressDirtyRefProp ?? ownSuppressDirtyRef;

    const { workbookSheets, pendingMap, tailMap, uncached } = useMemo(() => {
        const tails = new Map<string, FortuneCellData[]>();
        const trimmedSheets = sheets?.map((rawSheet) => {
            const sheet = withCoveringDims(rawSheet);
            const { sheet: trimmed, tail } = splitSparseTail(sheet);
            if (!tail.length) return trimmed;
            tails.set(String(sheet.id), tail);
            return clipCalcChainToGrid(trimmed);
        });
        const unchanged = !tails.size && trimmedSheets?.every((sheet, i) => sheet === sheets?.[i]);
        const resolvedSheets = unchanged ? sheets : trimmedSheets;
        const uncached = findUncachedFormulas(resolvedSheets);
        if (!resolvedSheets || resolvedSheets.length < 2 || totalCells(resolvedSheets) <= cellThreshold) {
            return { workbookSheets: resolvedSheets, pendingMap: new Map<string, FortuneSheet>(), tailMap: tails, uncached };
        }
        // Mirror Fortune-Sheet's ensureSheetIndex: when no sheet carries
        // status === 1, the library activates the FIRST sheet at mount — so
        // that is the one sheet that must never be a stub. Stubbing by
        // `status !== 1` alone blanked the whole workbook for saved/composed
        // sheet arrays where every status was 0.
        const statusActive = resolvedSheets.findIndex((s) => Number(s.status) === 1);
        const activeIndex = statusActive === -1 ? 0 : statusActive;
        const pending = new Map<string, FortuneSheet>();
        const stubbed = resolvedSheets.map((sheet, i) => {
            if (i === activeIndex) return sheet;
            pending.set(String(sheet.id), sheet);
            return makeStubSheet(sheet);
        });
        return { workbookSheets: stubbed, pendingMap: pending, tailMap: tails, uncached };
    }, [sheets, cellThreshold]);

    // Publish the derived map only once this render has committed. A render
    // React discards (StrictMode double-render, an abandoned concurrent
    // render) still runs the useMemo above but never reaches this effect, so
    // pendingRef always reflects the committed workbook.
    useEffect(() => {
        pendingRef.current = pendingMap;
        tailRef.current = tailMap;
        uncachedRef.current = uncached;
    }, [pendingMap, tailMap, uncached]);

    /**
     * Drain all pending stubs into the live workbook, one sheet per tick, then
     * recalculate formulas that arrived without a cached value (needs every stub
     * hydrated first so cross-sheet references resolve). Known limit: formulas parked
     * outside the grid by splitSparseTail are not recalculated.
     * Idempotent and safe to call repeatedly (StrictMode double-effects): each
     * sheet is removed from the queue before it is applied. Captures the
     * queue once per call — if a later commit publishes a new map before this
     * drain finishes, `pendingRef.current` no longer matches the captured
     * `queue` and the stale drain stops instead of touching the new workbook.
     */
    const hydratePending = useCallback(() => {
        const queue = pendingRef.current;
        const uncached = uncachedRef.current;
        const recalc = () =>
            recalculateUntilStable({ workbookRef, uncached, suppressDirtyRef, isStale: () => pendingRef.current !== queue });
        const drain = () => {
            const wb = workbookRef.current;
            if (!wb || pendingRef.current !== queue || queue.size === 0) return;
            const next = queue.entries().next();
            if (next.done) return;
            const [id, sheet] = next.value;
            queue.delete(id);
            // celldata path of updateSheet: expands to the payload's
            // row/column and registers formulas sparsely, keeping the stub's
            // config (borders/merges/widths) intact. Suppressed synchronously
            // around the call — updateSheet emits onOp ops path-identical to a
            // real edit (see suppressDirtyRef above).
            const release = holdDirtySuppression(suppressDirtyRef);
            try {
                // A row/column insert, delete or undo nulls Fortune's formula map so the next edit
                // rebuilds it from every sheet's calcChain; updateSheet would otherwise recreate it
                // holding only this sheet's formulas, and the rebuild would never fire.
                const mapWasEmpty = isEmpty(getInternals(wb)?.getContext().formulaCache.formulaCellInfoMap);
                // Quiet: hydration is bookkeeping — no undo entry (Ctrl+Z would re-stub the tab and
                // arm autosave with an empty sheet) and no onOp emission.
                produceQuietly(
                    wb,
                    (ctx) => {
                        core.api.updateSheet(ctx, [sheet]);
                        // updateSheet's celldata path registers the formulas but never copies the
                        // payload's calcChain, so restore it or the sheet's formulas stop recalculating.
                        const index = getSheetIndex(ctx, id);
                        const file = index == null ? undefined : ctx.luckysheetfile[index];
                        if (file && sheet.calcChain?.length) file.calcChain = sheet.calcChain;
                        // ...nor its row/column: the stub's 50×26 stayed, and Ctrl+Arrow stopped at row 50.
                        if (file) {
                            file.row = Math.max(sheet.row ?? 0, file.data?.length ?? 0);
                            file.column = Math.max(sheet.column ?? 0, file.data?.[0]?.length ?? 0);
                        }
                        if (mapWasEmpty) ctx.formulaCache.formulaCellInfoMap = null;
                    },
                    () => wb.updateSheet([sheet]),
                );
            } finally {
                release();
            }
            if (queue.size) setTimeout(drain, HYDRATE_TICK_MS);
            else if (uncached.length) setTimeout(recalc, HYDRATE_TICK_MS); // let the last updateSheet commit first
        };
        if (queue.size) setTimeout(drain, FIRST_HYDRATE_DELAY_MS);
        else if (uncached.length) setTimeout(recalc, FIRST_HYDRATE_DELAY_MS);
    }, [workbookRef, suppressDirtyRef]);

    /** Replace still-stubbed live sheets with their full pending originals and
     *  hand back the cells parked outside the grid by splitSparseTail. */
    const getFullSheets = useCallback((live: FortuneSheet[]): FortuneSheet[] => {
        if (!pendingRef.current.size && !tailRef.current.size) return live;
        return live.map((liveSheet) => {
            const id = String(liveSheet.id);
            const sheet = pendingRef.current.get(id) ?? liveSheet;
            const tail = tailRef.current.get(id);
            if (!tail) return sheet;
            const withCelldata = toCelldataSheet(sheet);
            return { ...withCelldata, celldata: [...(withCelldata.celldata ?? []), ...tail] };
        });
    }, []);

    return { workbookSheets, getFullSheets, hydratePending };
};
